package com.karyadina.model

import com.karyadina.util.currentTimeMillis
import kotlinx.serialization.Serializable

/**
 * Mastery levels for spaced repetition
 */
enum class MasteryLevel(val displayName: String, val reviewIntervalDays: Int) {
    NEW("New", 0),
    LEARNING("Learning", 1),
    REVIEW("Review", 3),
    FAMILIAR("Familiar", 7),
    KNOWN("Known", 14),
    MASTERED("Mastered", 30)
}

/**
 * Exam types
 */
enum class ExamType(val displayName: String) {
    GATE("GATE"),
    NET("NET/JRF"),
    UPSC("UPSC"),
    OTHER("Other")
}

/**
 * Subject category
 */
@Serializable
data class FlashCard_Subject(
    val id: String,
    val name: String,
    val examType: String, // ExamType as string for serialization
    val color: String, // Hex color code
    val totalUnits: Int = 0,
    val completedUnits: Int = 0,
    val createdAt: Long = currentTimeMillis(),
    val updatedAt: Long = currentTimeMillis()
)

/**
 * Unit/Chapter within a subject
 */
@Serializable
data class Unit(
    val id: String,
    val subjectId: String,
    val name: String,
    val order: Int, // For ordering units
    val totalCards: Int = 0,
    val masteredCards: Int = 0,
    val createdAt: Long = currentTimeMillis(),
    val updatedAt: Long = currentTimeMillis()
)

/**
 * Flashcard with spaced repetition data
 */
@Serializable
data class Flashcard(
    val id: String,
    val unitId: String,
    val subjectId: String,
    val question: String,
    val answer: String,
    val hint: String? = null,
    val tags: List<String> = emptyList(),
    val masteryLevel: String = MasteryLevel.NEW.name,
    val lastReviewedAt: Long? = null,
    val nextReviewDate: Long? = null,
    val reviewCount: Int = 0,
    val correctCount: Int = 0,
    val incorrectCount: Int = 0,
    val createdAt: Long = currentTimeMillis(),
    val updatedAt: Long = currentTimeMillis(),
    val isFavorite: Boolean = false,
    val imagePath: String? = null, // Path to associated image (scanned note) - deprecated, use imagePaths
    val imagePaths: List<String> = emptyList(), // Multiple image paths (up to 4)
    val hasScannedContent: Boolean = false, // Flag to indicate card was created from scan
    
    // Formula-specific fields
    val formulaLatex: String? = null,           // LaTeX representation of formula
    val formulaImagePath: String? = null,       // Path to rendered formula image (SVG/PNG)
    val formulaDescription: String? = null,     // Human-readable description of formula
    val formulaVariables: Map<String, String> = emptyMap(), // Variable definitions (e.g., "F" -> "Force", "m" -> "mass")
    val formulaType: String? = null,            // FormulaType enum as string
    val hasFormula: Boolean = false             // Quick check if card contains formula
)

/**
 * Review session
 */
@Serializable
data class ReviewSession(
    val id: String,
    val subjectId: String,
    val unitId: String? = null, // null means all units
    val startTime: Long,
    val endTime: Long? = null,
    val cardsReviewed: Int = 0,
    val correctAnswers: Int = 0,
    val incorrectAnswers: Int = 0,
    val completed: Boolean = false
)

/**
 * Statistics per subject
 */
@Serializable
data class SubjectStats(
    val subjectId: String,
    val totalCards: Int = 0,
    val newCards: Int = 0,
    val learningCards: Int = 0,
    val reviewCards: Int = 0,
    val masteredCards: Int = 0,
    val dueForReview: Int = 0,
    val averageAccuracy: Float = 0f,
    val totalReviewTime: Int = 0, // minutes
    val lastReviewDate: Long? = null
)

/**
 * Overall flashcard statistics
 */
@Serializable
data class FlashcardStats(
    val totalSubjects: Int = 0,
    val totalUnits: Int = 0,
    val totalCards: Int = 0,
    val cardsToReview: Int = 0,
    val masteredCards: Int = 0,
    val currentStreak: Int = 0,
    val longestStreak: Int = 0,
    val totalReviewSessions: Int = 0,
    val averageAccuracy: Float = 0f
)
